import {
  MAP_BURN_MOREL_SPECIES_QUERIES,
  MAP_QUICK_SPECIES_LOOKUP,
  MAP_WILDFIRE_LAYER_QUERIES,
} from "@/constants/mapSearch";
import { BURN_MOREL_PANE_SLUG } from "@/constants/burnMorelForecast";
import {
  mapFieldGuideDetailPath,
  publicFieldGuideDetailPath,
  publicFieldGuideListPath,
} from "@/constants/fieldGuideRoutes";
import type { Species } from "@/data/species/types/Species";
import { getCategoryUrlFromKey } from "@/utils/speciesCategoryUtils";
import { isFieldGuideVisible } from "@/utils/speciesUtils";
import { searchAliasesForSlug } from "@/utils/species/speciesSearchAliases";

export type SpeciesSearchDestinationKind = "map" | "article" | "browse" | "maps-home";

export type SpeciesSearchDestination = {
  kind: SpeciesSearchDestinationKind;
  path: string;
};

const SUGGESTION_LIMIT = 8;

/** Lowercase, drop accents and punctuation, and collapse separators to spaces. */
export function normalizeSpeciesSearchQuery(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

const TOKEN_SPELLING: Record<string, string> = {
  gray: "grey",
  mould: "mold",
  sulfur: "sulphur",
};

function singularizeSearchToken(token: string): string {
  if (token.length <= 3) return token;
  if (token.endsWith("ies") && token.length > 4) {
    return `${token.slice(0, -3)}y`;
  }
  if (token.endsWith("sses") && token.length > 5) {
    return token.slice(0, -2);
  }
  if (/(?:ses|xes|zes|ches|shes)$/.test(token) && token.length > 4) {
    return token.slice(0, -2);
  }
  if (
    token.endsWith("s") &&
    !token.endsWith("ss") &&
    !token.endsWith("us") &&
    !token.endsWith("is")
  ) {
    return token.slice(0, -1);
  }
  return token;
}

function canonicalizeSearchToken(token: string): string {
  const singular = singularizeSearchToken(token);
  return TOKEN_SPELLING[singular] ?? singular;
}

function searchTokens(normalized: string): string[] {
  if (!normalized) return [];
  return normalized
    .split(" ")
    .filter((token) => token.length > 1)
    .map(canonicalizeSearchToken);
}

function compactSearchText(normalized: string): string {
  return normalized.replace(/ /g, "");
}

function searchFields(species: Pick<
  Species,
  "name" | "scientificName" | "slug" | "alternateNames"
>): string[] {
  return [
    species.name,
    species.scientificName,
    species.slug,
    ...(species.alternateNames ?? []),
    ...searchAliasesForSlug(species.slug),
  ]
    .filter((value): value is string => Boolean(value && String(value).trim()))
    .map((value) => normalizeSpeciesSearchQuery(value))
    .filter(Boolean);
}

function fieldsMatchQuery(query: string, fields: string[]): boolean {
  if (fields.some((field) => field.includes(query))) return true;
  const compactQuery = compactSearchText(query);
  if (
    compactQuery.length >= 4 &&
    fields.some((field) => compactSearchText(field).includes(compactQuery))
  ) {
    return true;
  }
  return queryTokensMatchSpecies(query, fields);
}

function tokenSignature(normalized: string): string {
  return searchTokens(normalized).join(" ");
}

function speciesTokenSet(fields: string[]): Set<string> {
  return new Set(fields.flatMap(searchTokens));
}

function queryTokensMatchSpecies(query: string, fields: string[]): boolean {
  const tokens = searchTokens(query);
  if (tokens.length === 0) return false;
  const speciesTokens = speciesTokenSet(fields);
  return tokens.every((token) => speciesTokens.has(token));
}

function matchesQuerySet(raw: string, terms: Set<string>): boolean {
  const query = normalizeSpeciesSearchQuery(raw);
  if (!query) return false;
  for (const term of terms) {
    if (normalizeSpeciesSearchQuery(term) === query) return true;
  }
  return false;
}

/** Burn Morels species pane — not the burns layer toggle. */
export function isBurnMorelSearchQuery(raw: string): boolean {
  return matchesQuerySet(raw, MAP_BURN_MOREL_SPECIES_QUERIES);
}

/** Wildfire burns layer, without opening a species pane. */
export function isWildfireLayerSearchQuery(raw: string): boolean {
  return matchesQuerySet(raw, MAP_WILDFIRE_LAYER_QUERIES);
}

export function speciesMatchesSearchQuery(
  species: Pick<Species, "name" | "scientificName" | "slug" | "alternateNames">,
  rawQuery: string,
): boolean {
  const query = normalizeSpeciesSearchQuery(rawQuery);
  if (!query) return true;
  return fieldsMatchQuery(query, searchFields(species));
}

export function filterSpeciesBySearchQuery<T extends Pick<
  Species,
  "name" | "scientificName" | "slug" | "alternateNames" | "hiddenFromFieldGuide"
>>(
  species: T[],
  rawQuery: string,
  options: { fieldGuideOnly?: boolean } = {},
): T[] {
  const list = options.fieldGuideOnly
    ? species.filter(isFieldGuideVisible)
    : species;
  const query = normalizeSpeciesSearchQuery(rawQuery);
  if (!query) return list;
  return list
    .map((entry) => ({
      entry,
      score: scoreSpeciesMatch(entry, query),
    }))
    .filter((item) => item.score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.entry.name.localeCompare(b.entry.name);
    })
    .map((item) => item.entry);
}

function scoreSpeciesMatch(
  species: Pick<Species, "name" | "scientificName" | "slug" | "alternateNames">,
  query: string,
): number {
  const fields = searchFields(species);
  const querySignature = tokenSignature(query);
  if (
    fields.some(
      (field) => field === query || tokenSignature(field) === querySignature,
    )
  ) {
    return 3;
  }
  if (fields.some((field) => field.startsWith(query))) return 2;
  const compactQuery = compactSearchText(query);
  if (
    compactQuery.length >= 4 &&
    fields.some((field) => compactSearchText(field) === compactQuery)
  ) {
    return 3;
  }
  if (fields.some((field) => field.includes(query))) return 1;
  if (
    compactQuery.length >= 4 &&
    fields.some((field) => compactSearchText(field).includes(compactQuery))
  ) {
    return 1;
  }
  if (queryTokensMatchSpecies(query, fields)) return 1;
  return 0;
}

/**
 * 3 exact (or same words), 2 prefix, 1 loose match, 0 no match. Every search
 * box sorts species by this, highest first, then by name. Mobile mirrors it in
 * `speciesSearchScore` (mobile-app/lib/utils/species_name_search.dart).
 */
export function speciesSearchScore(
  species: Pick<Species, "name" | "scientificName" | "slug" | "alternateNames">,
  rawQuery: string,
): number {
  const query = normalizeSpeciesSearchQuery(rawQuery);
  if (!query) return 0;
  return scoreSpeciesMatch(species, query);
}

export function rankSpeciesSearchSuggestions<T extends Pick<
  Species,
  "name" | "scientificName" | "slug" | "alternateNames" | "hiddenFromFieldGuide"
>>(species: T[], rawQuery: string, limit = SUGGESTION_LIMIT): T[] {
  const query = normalizeSpeciesSearchQuery(rawQuery);
  if (!query) return [];

  return species
    .filter(isFieldGuideVisible)
    .map((entry) => ({
      entry,
      score: scoreSpeciesMatch(entry, query),
    }))
    .filter((item) => item.score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.entry.name.localeCompare(b.entry.name);
    })
    .slice(0, limit)
    .map((item) => item.entry);
}

export function resolveBestSpeciesMatch<T extends Pick<
  Species,
  "name" | "scientificName" | "slug" | "alternateNames"
>>(species: T[], rawQuery: string): T | null {
  const query = normalizeSpeciesSearchQuery(rawQuery);
  if (!query) return null;

  const quick = MAP_QUICK_SPECIES_LOOKUP[query];
  const exact =
    species.find((entry) => scoreSpeciesMatch(entry, query) === 3) ??
    (quick
      ? species.find((entry) => entry.slug?.trim().toLowerCase() === quick.slug)
      : undefined);
  if (exact) return exact;

  const prefixMatches = species.filter(
    (entry) => scoreSpeciesMatch(entry, query) === 2,
  );
  if (prefixMatches.length === 1) return prefixMatches[0];

  const looseMatches = species.filter(
    (entry) => scoreSpeciesMatch(entry, query) > 0,
  );
  if (looseMatches.length === 1) return looseMatches[0];

  return null;
}

export function fieldGuideSearchPath(rawQuery: string): string {
  const trimmed = rawQuery.trim();
  if (!trimmed) return publicFieldGuideListPath();
  return `${publicFieldGuideListPath()}?q=${encodeURIComponent(trimmed)}`;
}

export function fieldGuideArticlePath(
  species: Pick<Species, "slug" | "category">,
): string | null {
  if (!species.slug) return null;
  return publicFieldGuideDetailPath(
    getCategoryUrlFromKey(species.category),
    species.slug,
  );
}

export function resolveHomepageSpeciesSearch(
  species: Pick<
    Species,
    "name" | "scientificName" | "slug" | "alternateNames" | "category"
  >[],
  rawQuery: string,
): SpeciesSearchDestination {
  const trimmed = rawQuery.trim();
  if (!trimmed) {
    return { kind: "maps-home", path: "/maps" };
  }

  if (isWildfireLayerSearchQuery(trimmed)) {
    return { kind: "maps-home", path: "/maps?layer=burns" };
  }

  if (isBurnMorelSearchQuery(trimmed)) {
    return {
      kind: "map",
      path: mapFieldGuideDetailPath(BURN_MOREL_PANE_SLUG),
    };
  }

  const match = resolveBestSpeciesMatch(species, trimmed);
  if (match?.slug) {
    return { kind: "map", path: mapFieldGuideDetailPath(match.slug) };
  }

  return { kind: "browse", path: fieldGuideSearchPath(trimmed) };
}

export function resolveFieldGuideSpeciesSearch(
  species: Pick<
    Species,
    | "name"
    | "scientificName"
    | "slug"
    | "alternateNames"
    | "category"
    | "hiddenFromFieldGuide"
  >[],
  rawQuery: string,
): SpeciesSearchDestination {
  const trimmed = rawQuery.trim();
  if (!trimmed) {
    return { kind: "browse", path: publicFieldGuideListPath() };
  }

  const visible = species.filter(isFieldGuideVisible);
  const match = resolveBestSpeciesMatch(visible, trimmed);
  const articlePath = match ? fieldGuideArticlePath(match) : null;
  if (articlePath) {
    return { kind: "article", path: articlePath };
  }

  return { kind: "browse", path: fieldGuideSearchPath(trimmed) };
}
